// Client type (child/adult) of leads and of the sessions they originate, plus the breakdown chips derived
// from it. Shared by digital-marketing and reception-kpis (see README, "Métricas partilhadas"): both
// dashboards show the same cards and must split the value the same way.

// Lead type from the leads.client_type field (filled in manually by the call center — the source of truth
// at lead level; many leads have none because they never answer / age unknown). "adult" → Adulto, "child" →
// Criança, anything else/null → Desconhecido. Do NOT use the birthday age for lead-type charts.
export const leadTypeFromClientType = (clientType: string | null | undefined) => {
    const labelByClientType: Record<string, string> = { adult: "Adulto", child: "Criança" };
    const leadType = labelByClientType[clientType || ""] || "Desconhecido";
    return leadType;
};

export type SessionLeadType = {
    lead: { client_type: string | null } | null;
    treatment: { client: { lead: { client_type: string | null } | null } | null } | null;
};

// Client type of a session: the linked lead when there is one (first visits) and, when there is not, the
// lead that originated the treatment's client (sessions downstream in the funnel — e.g. the evaluation
// consultations hang off the treatment and NONE has a direct lead; without this second path they all fell
// into "Sem tipo").
export const sessionClientType = (session: SessionLeadType) => {
    const clientType = session.lead?.client_type ?? session.treatment?.client?.lead?.client_type;
    return clientType;
};

// Minimum fields needed to resolve a session's client type through both paths above.
export const SESSION_LEAD_TYPE_FIELDS = "id,lead.client_type,treatment.client.lead.client_type";

// Splits a universe into child / adult / no type. Each entry carries the client_type and its WEIGHT: 1 for
// raw items (one session via lead.client_type) or the group count for aggregate rows grouped by client_type
// — so the same rollup serves the lead cards (aggregate) and the session cards (raw items).
export const leadTypeCounts = (entries: Array<{ clientType: string | null | undefined; count: number }>) => {
    const counts = { child: 0, adult: 0, unknown: 0 };
    entries.forEach((entry) => {
        const leadType = leadTypeFromClientType(entry.clientType);
        if (leadType === "Criança") counts.child += entry.count;
        else if (leadType === "Adulto") counts.adult += entry.count;
        else counts.unknown += entry.count;
    });
    return counts;
};

// Chips that watch campaign segmentation (the business goal is more children than adults). Absolute
// numbers, and "Sem tipo" only shows up when it exists — untriaged leads have no client_type yet.
export const leadTypeChips = (counts: { child: number; adult: number; unknown: number }) => {
    const chips = [`Crianças: ${counts.child}`, `Adultos: ${counts.adult}`];
    if (counts.unknown > 0) chips.push(`Sem tipo: ${counts.unknown}`);
    return chips;
};

export const leadTypeChipsFromSessions = (sessions: Array<SessionLeadType>) => {
    const chips = leadTypeChips(leadTypeCounts(sessions.map((session) => ({ clientType: sessionClientType(session), count: 1 }))));
    return chips;
};

// Text of the ⓘ explanations of the cards carrying these chips — shared so both dashboards say exactly the
// same about the same breakdown. User-facing, therefore pt-PT.
export const LEAD_TYPE_CHIPS_TEXT =
    'Os chips "Crianças"/"Adultos" repartem o valor pelo campo "tipo de cliente" da lead (leads.client_type) — nunca pela idade, que muitas leads não têm. Números absolutos; "Sem tipo" é o que ainda não foi classificado e só aparece quando existe, de forma a que os três somem sempre ao valor do cartão.';

export const SESSION_LEAD_TYPE_TEXT =
    'As sessões não têm tipo de cliente próprio: cada sessão herda o tipo da LEAD que a originou — a lead ligada à sessão quando existe (1ªs visitas) ou, quando não existe, a lead que originou o cliente do tratamento (sessões a jusante do funil). Só cai em "Sem tipo" quando nenhuma das duas vias dá tipo.';

export const LEAD_TYPE_CHIPS_WARNING =
    'O "tipo de cliente" é preenchido pelo call center quando trabalha a lead, não na criação: nas janelas mais recentes (sobretudo o cartão de hoje) uma fatia grande fica em "Sem tipo" e vai sendo classificada ao longo do dia — medido a 2026-08-03: 10 de 20 leads de hoje sem tipo (50%) contra 42 de 124 nos últimos 7 dias (34%) e ~4% nas avaliações dos últimos 30 dias. Compara Crianças com Adultos entre si e nunca com o total do cartão; para ler a segmentação das campanhas usa a janela de 7 dias ou maior, onde a triagem já está feita. O campo só passou a ser preenchido a partir de setembro de 2025.';
